import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCandidateT } from '../i18n/candidate';

/**
 * Prevents browser back navigation: when the candidate clicks back, they remain on the same page.
 * If onBackAttempt is provided, that callback is run (e.g. to show a custom modal) instead of alert.
 * Used across screening flow (apply, resume, hub, assessment, AI interview).
 * @param {boolean} enabled - When true (default), back is disabled (user stays on current page).
 * @param {{ onBackAttempt?: () => void }} [options] - If onBackAttempt is set, it is called when user presses back instead of showing the native alert.
 */
export function usePreventBack(enabled = true, options = {}) {
  const { onBackAttempt } = options;
  const location = useLocation();
  const navigate = useNavigate();
  const protectedRef = useRef({ pathname: '', search: '', state: null });
  const onBackAttemptRef = useRef(onBackAttempt);
  onBackAttemptRef.current = onBackAttempt;
  // Read through a ref so a language change does not re-run the history effect below.
  const t = useCandidateT();
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const message =
      "You're in the middle of your screening. Closing or leaving this page may interrupt your progress and could affect your application. If possible, please continue until you see the final confirmation screen.";

    const pathname = location.pathname;
    const search = location.search;
    const path = pathname + search;

    protectedRef.current = {
      pathname,
      search,
      state: location.state,
    };

    // Add a history entry so the first Back press stays on this page (same URL)
    window.history.pushState(null, '', path);

    const handlePop = () => {
      const { pathname: toPath, search: toSearch, state: toState } = protectedRef.current;
      const pathToRestore = toPath + toSearch;
      // Re-push this page so Back does nothing: candidate remains on same page
      window.history.pushState(null, '', pathToRestore);
      navigate(pathToRestore, { replace: true, state: toState });
      const callback = onBackAttemptRef.current;
      if (typeof callback === 'function') {
        callback();
      } else {
        try {
          // eslint-disable-next-line no-alert
          window.alert(tRef.current(message));
        } catch {
          // In very locked-down environments, alert may be blocked; ignore.
        }
      }
    };

    // Use capture so we run before React Router; back button then has no visible effect
    window.addEventListener('popstate', handlePop, true);
    return () => window.removeEventListener('popstate', handlePop, true);
  }, [enabled, location.pathname, location.search, navigate]);
}
